import { BoxItem } from 'box-ui-elements/es';
import { isEmpty, sortBy } from 'lodash';
import { ReactNode } from 'react';
import { Button, Divider, NewMenu, SearchBar, Stack } from '@common-components';
import {
  BoxTemplateLabels,
  FileType,
  SubmissionDraftType,
  SubmissionMarketRequestStatus,
  SubmissionMethod,
} from 'enums';
import { useOptionsMenu } from 'hooks';
import { useSearchSubmissionDrafts } from 'hooks/api/submission-draft';
import { messages } from 'i18n';
import { QuoteComparisonDraftMetadata, SubmissionDraft } from 'types';
import { getDisplayDateWithHoursAmPm } from 'utils/date-utils';
import { getFileMetadataField } from 'utils/files-utils';
import { MenuItemProps } from 'components/menu';
import Filter, { FilterButtonTypes } from 'broker/components/common/Filter';
import Sort from 'broker/components/common/Sort';
import { isMarketingReportEnabled } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/MarketingReport/utils';
import SubmitButton from 'broker/pages/SubmissionWorkspacePage/components/TabsPanel/common/MarketRequestsWorkspace/components/SubmitButton';
import {
  MarketsSortAndFilterProps,
  sorters,
} from 'broker/pages/SubmissionWorkspacePage/components/TabsPanel/common/MarketRequestsWorkspace/useMarketRequestsSortAndFilter';
import { useFetchSubmissionFiles } from 'broker/pages/SubmissionWorkspacePage/hooks/useFetchSubmissionFiles';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import CoveragesFilter from './CoveragesFilter';
import { useGetMarketingUpdateMenuItem } from './useGetMarketingUpdateMenuItem';

interface HeaderProps {
  headerButtons?: ReactNode;
  sortingProps: MarketsSortAndFilterProps;
  showSearchAndFilter: boolean;
  onSelectSubmissionMethod: (method: SubmissionMethod) => void;
  onSelectQuoteComparison: (expiringPolicyFile?: BoxItem) => void;
  onSelectSavedComparison: (draft: SubmissionDraft) => void;
}

export default function Header({
  headerButtons,
  sortingProps,
  showSearchAndFilter = true,
  onSelectSubmissionMethod,
  onSelectQuoteComparison,
  onSelectSavedComparison,
}: HeaderProps) {
  const { markets, partialSubmission } = useSubmissionsWorkspace();

  const { items, isLoading: boxItemsLoading } = useFetchSubmissionFiles();
  const marketingUpdateMenuItem = useGetMarketingUpdateMenuItem();

  const { items: quoteComparisonDrafts, isLoading: quoteComparisonDraftsLoading } = useSearchSubmissionDrafts({
    filter: {
      submissionId: partialSubmission?.id,
      draftType: SubmissionDraftType.QuoteComparison,
    },
    enabled: !!partialSubmission?.id,
  });

  // filter out only expiring policy files
  const filteredItems = items.filter(
    (item) => getFileMetadataField(item, BoxTemplateLabels.FileType) === FileType.ExpiringProgram,
  );

  const {
    filters,
    handleStatusFilterChange,
    search,
    setSearch,
    sortType,
    setSortType,
    isAllFilters,
    sortedAndFilteredItems,
    setAllStatusSelected,
    coverageLineFilters,
    handleVisibleCoverageChange,
  } = sortingProps;
  const optionsMenuState = useOptionsMenu();

  const readyToMarketRequests = sortedAndFilteredItems?.filter(
    (marketRequest) => marketRequest.status === SubmissionMarketRequestStatus.ReadyToMarket,
  );

  const showSubmitButton = readyToMarketRequests?.length > 0;

  const getMenuOptions = (expiringPolicyFiles?: BoxItem[]) => {
    const menuItems: MenuItemProps[] = [];
    menuItems.push(marketingUpdateMenuItem);

    // Add Saved Comparisons menu item
    if (isEmpty(quoteComparisonDrafts)) {
      menuItems.push({
        label: messages.submissionWorkspace.menus.share.savedComparisons,
        disabled: true,
        disabledTooltipContent: messages.submissionWorkspace.menus.share.noSavedQuoteComparisons,
        tooltipPlacement: 'right',
      });
    } else {
      const draftsMenuItems: MenuItemProps[] = sortBy(quoteComparisonDrafts, 'updatedAt')
        .reverse()
        .map((draft) => ({
          key: draft.id,
          label: (draft.metadata.quoteComparison as QuoteComparisonDraftMetadata).title,
          subLabel: `${messages.general.lastUpdated} ${getDisplayDateWithHoursAmPm(draft.updatedAt)}`,
          onClick: () => onSelectSavedComparison(draft),
        }));

      menuItems.push({
        label: messages.submissionWorkspace.menus.share.savedComparisons,
        subMenu: [
          {
            key: 'saved-comparisons',
            items: draftsMenuItems,
          },
        ],
      });
    }

    menuItems.push({
      label: messages.submissionWorkspace.menus.share.quoteComparison,
      onClick: onSelectQuoteComparison,
    });
    if (!isEmpty(expiringPolicyFiles)) {
      expiringPolicyFiles?.map((file) =>
        menuItems.push({
          label: messages.submissionWorkspace.menus.share.compareToExpiringPolicy,
          subLabel: file.name,
          onClick: () => onSelectQuoteComparison(file),
        }),
      );
    } else {
      menuItems.push({
        label: messages.submissionWorkspace.menus.share.compareToExpiringPolicy,
        disabled: true,
        disabledTooltipContent: messages.submissionWorkspace.menus.share.noExpiringPolicyFiles,
        tooltipPlacement: 'right',
      });
    }

    return menuItems;
  };

  return (
    <Stack py={2} px={2} gap={1}>
      {showSearchAndFilter && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} alignItems="center">
              <SearchBar
                value={search}
                placeholder={messages.general.searchMarkets}
                onChange={(value: string) => setSearch(value)}
              />
              <Filter
                title="Status"
                buttonType={FilterButtonTypes.Dropdown}
                filters={filters}
                handleFilterChange={handleStatusFilterChange}
                handleAllSelected={setAllStatusSelected}
                isAllFilters={isAllFilters}
              />
              <Divider orientation="vertical" flexItem variant="middle" sx={{ mx: 1, my: 1.4 }} />
              <Sort menuPlacement="bottom-left" setSortType={setSortType} sortType={sortType} sorters={sorters} />
            </Stack>
            <Stack direction="row" gap={1}>
              {showSubmitButton && (
                <SubmitButton
                  onSelectSubmissionMethod={onSelectSubmissionMethod}
                  requestsCount={readyToMarketRequests?.length ?? 0}
                />
              )}
              <Button
                disabledTooltipContent={
                  boxItemsLoading ? '' : messages.marketingReportPage.editMode.disabled.description
                }
                disabled={!isMarketingReportEnabled(markets)}
                variant="contained"
                color="secondary"
                onClick={optionsMenuState.openMenu}
                loading={boxItemsLoading || quoteComparisonDraftsLoading}
              >
                {messages.buttons.reports}
              </Button>
              {headerButtons}
            </Stack>
            <NewMenu
              placement="bottom-right"
              optionsMenuState={optionsMenuState}
              menuItems={[
                {
                  key: 'share',
                  items: getMenuOptions(filteredItems),
                },
              ]}
            />
          </Stack>
          <CoveragesFilter
            coverageFilter={coverageLineFilters}
            handleVisibleLabelChange={handleVisibleCoverageChange}
          />
        </>
      )}
    </Stack>
  );
}
